import { useEffect, useRef, useState } from 'react';

export interface StatusEvent {
  text: string;
  tool?: string;
  count?: number;
  /** Local timestamp the event was received. */
  receivedAt: number;
}

const MIN_DISPLAY_MS = 1200;

/**
 * Displays the most recent status message with a minimum hold time.
 * If a new event arrives before the current one has been visible for
 * MIN_DISPLAY_MS, the new event is queued.
 */
export function StatusDisplay({
  events,
  inFlight,
}: {
  events: StatusEvent[];
  inFlight: boolean;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const queueRef = useRef<StatusEvent[]>([]);
  const lastShownAtRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // We intentionally trigger only on event-count change so a re-render
    // mid-hold doesn't reset the queue. `drain` is stable enough for this
    // local component (no state captured at definition time besides refs).
    queueRef.current = [...events];
    drain();
  }, [events.length]); // eslint-disable-line

  useEffect(() => {
    if (!inFlight) {
      // Clear when the request finishes
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      setShown(null);
      queueRef.current = [];
    }
  }, [inFlight]);

  function drain() {
    if (timerRef.current) return;
    if (queueRef.current.length === 0) return;
    const elapsed = Date.now() - lastShownAtRef.current;
    const wait = Math.max(0, MIN_DISPLAY_MS - elapsed);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const next = queueRef.current.shift();
      if (next) {
        setShown(next.text);
        lastShownAtRef.current = Date.now();
      }
      drain();
    }, wait);
  }

  if (!inFlight && !shown) return null;

  // Render the indicator as a left-aligned chat bubble matching the
  // assistant's style — same border/shadow/rounded-2xl as MessageBubble.
  // Inside the bubble: animated typing dots + the current status text.
  // This makes the "thinking" state feel like part of the conversation
  // flow rather than a floating system pill.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Assistant is typing"
      className="mx-auto w-full max-w-3xl"
    >
      <div className="flex justify-start">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <TypingDots />
          <span className="text-sm text-slate-600">{shown ?? 'On the case…'}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Three-dot typing indicator. Each dot pulses on a staggered delay so
 * the eye reads it as left-to-right motion (same pattern iMessage /
 * WhatsApp / Slack use). Tailwind's `animate-bounce` gives the right
 * feel; we scale + dim it to match a quiet chat-bubble interior.
 */
function TypingDots() {
  return (
    <span className="flex items-center gap-1" aria-hidden="true">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-wb-600 motion-safe:animate-bounce"
        style={{ animationDelay: '0ms', animationDuration: '1.2s' }}
      />
      <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-wb-600 motion-safe:animate-bounce"
        style={{ animationDelay: '150ms', animationDuration: '1.2s' }}
      />
      <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-wb-600 motion-safe:animate-bounce"
        style={{ animationDelay: '300ms', animationDuration: '1.2s' }}
      />
    </span>
  );
}
